import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, Dialog, Divider, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { SyntheticEvent } from "react";
import { CSVLink } from "react-csv";
import {
  DeepPartial,
  FieldValues,
  FormProvider,
  UnpackNestedValue,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AtonUnit } from "~/api/GeneratedApi";
import PaddedBox from "~/components/PaddedBox";
import { emitter } from "~/utils/EventEmitter";
import { AtoNsMapDialog } from "../aton-units-page/AtoNsMapDialog";
import { CircularLoadingWithLabel } from "../ProjectsPage/components/CircularLoadingWithLabel";
import { SearchContainer } from "./AtonSearchFormContainers/SearchContainer";

type GenericSearchPageProps<T> = {
  searchContainers: SearchFormType[];
  defaultValues: UnpackNestedValue<DeepPartial<T>>;
  tableContent: JSX.Element;
  pageTitle: string;
  isDisabled?: boolean;
  setSearchOptions?: React.Dispatch<React.SetStateAction<T | undefined>>;
  isLoading?: boolean;
  atonUnits: AtonUnit[];
  csvData?: Record<string, unknown>[];
};

export type SearchFormType = {
  title: string;
  children: JSX.Element;
};
export const useStyles = makeStyles((theme) => ({
  buttonStyles: {
    // Copy the desired Button styles here
    // For example:
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    borderRadius: "8px",
    padding: "6px 16px",
    marginBottom: "5px",
    textDecoration: "none",
    "&:hover": {
      boxShadow:
        "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
    },
    // ...
  },
}));
export const GenericSearchPage = <
  TFieldValues extends FieldValues = FieldValues,
>({
  searchContainers,
  defaultValues,
  tableContent,
  pageTitle,
  isDisabled,
  isLoading = false,
  setSearchOptions,
  atonUnits,
  csvData = [],
}: GenericSearchPageProps<TFieldValues>): JSX.Element => {
  const userForm = useForm<TFieldValues>({
    defaultValues,
  });

  const { t } = useTranslation("securityDeviceSearch");
  const [hideTable, setHideTable] = React.useState(true);

  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);

  const onClose = React.useCallback(() => {
    setDialogOpen(false);
  }, [setDialogOpen]);

  const onSubmit = userForm.handleSubmit((data) => {
    setSearchOptions?.(data as React.SetStateAction<TFieldValues | undefined>);
    setHideTable(false);
  });

  function onPromise<P>(promise: (event: SyntheticEvent) => Promise<P>) {
    return (event: SyntheticEvent) => {
      if (promise) {
        promise(event).catch((error) => {
          console.log("Unexpected error", error);
        });
      }
    };
  }
  const handleClear = React.useCallback(() => {
    userForm.reset();
    emitter.emit("clear-search");
    setHideTable(true);
  }, [userForm]);

  const classes = useStyles();
  const showDialog = React.useCallback(() => {
    setDialogOpen(true);
  }, [setDialogOpen]);

  const tableButtons = React.useMemo(() => {
    return csvData && csvData?.length > 0 ? (
      <Box sx={{ display: "flex" }}>
        <Button
          onClick={showDialog}
          variant="contained"
          sx={{ ml: "auto", mb: "5px", mr: "5px" }}
        >
          <Typography>{t("showOnMap")}</Typography>
        </Button>
        <CSVLink
          filename={pageTitle}
          className={classes.buttonStyles}
          data={csvData}
          separator=";"
          uFEFF
        >
          <Typography> {t("fairwayReport:csvExport")}</Typography>
        </CSVLink>
      </Box>
    ) : null;
  }, [classes.buttonStyles, csvData, pageTitle, showDialog, t]);

  return (
    <>
      <Dialog
        sx={{ width: "100%", height: "100%" }}
        maxWidth="xl"
        open={dialogOpen}
        onClose={onClose}
        PaperProps={{ sx: { width: "80%", height: "80%" } }}
      >
        <AtoNsMapDialog
          onClose={onClose}
          fitToExtend
          atonUnits={atonUnits ?? []}
        />
      </Dialog>
      <FormProvider {...userForm}>
        <PaddedBox
          sx={{
            gap: "10px",
            display: "flex",
            width: { xs: "100%", md: "100%", lg: "100%", xl: "90%" },
            margin: "auto",
            flexDirection: "column",
          }}
        >
          <Box sx={{ mb: "15px" }}>
            <Typography variant="h4">
              {pageTitle}
              {isDisabled ? " (hakutoiminto ei käytössä!)" : ""}
            </Typography>
            <Divider />
          </Box>

          <form onSubmit={onPromise(onSubmit)}>
            <Box sx={{ display: "grid", gap: "15px" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit,minmax(400px,1fr))",
                  gap: "15px",
                }}
              >
                {searchContainers.map((searchBox) => (
                  <SearchContainer
                    key={searchBox.title}
                    title={searchBox.title}
                  >
                    {searchBox.children}
                  </SearchContainer>
                ))}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                <Button
                  color="warning"
                  variant="contained"
                  onClick={() => handleClear()}
                >
                  <Typography>
                    {t("securityDeviceSearch:clearButton")}
                  </Typography>
                </Button>
                <Button
                  startIcon={<SearchIcon />}
                  variant="contained"
                  type="submit"
                  //   onClick={handleSearch}
                >
                  <Typography>
                    {t("securityDeviceSearch:submitButton")}
                  </Typography>
                </Button>
              </Box>
            </Box>
          </form>

          <Box
            sx={{
              "& div": {
                // padding: "3px",
                // margin: "-3px",
                p: "0",
              },
              "& h6": {
                marginLeft: "10px",
              },
            }}
          >
            {!isDisabled && isLoading ? (
              <Box
                sx={{
                  height: "150px",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  display: "flex",
                }}
              >
                <CircularLoadingWithLabel
                  alignText="column"
                  text={t("general:isLoading")}
                />
              </Box>
            ) : (
              <Box>
                {tableButtons}
                {!hideTable && tableContent}
              </Box>
            )}
          </Box>
        </PaddedBox>
      </FormProvider>
    </>
  );
};

// type FormProps = Partial<Omit<UseFormReturn<FormData, unknown>, "register">> & {
//   register: UseFormRegister<FormData>;
// };
